import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AGENT_TEMPLATES } from "@/lib/agentTemplates";
import { modelLabel } from "@/lib/models";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Plus, ChatCircleDots, Phone, Robot, DotsThreeVertical, Trash, PencilSimple,
  CircleNotch, Books, ChatsCircle, Copy, BookmarkSimple, X, Sparkle, User,
} from "@phosphor-icons/react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

function StatusBadge({ status }) {
  const live = status === "live";
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${
      live ? "bg-green-50 text-green-700 border border-green-200" : "bg-zinc-100 text-zinc-500 border border-zinc-200"
    }`}>
      <span className={`h-1.5 w-1.5 rounded-full ${live ? "bg-green-500" : "bg-zinc-400"}`} />
      {live ? "Live" : "Utkast"}
    </span>
  );
}

export default function AgentsList() {
  const navigate = useNavigate();
  const { can, user } = useAuth();
  const canEdit = can("edit_agents");
  const isSuperAdmin = user?.role === "superadmin";
  const [agents, setAgents] = useState(null);
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  // selected: { kind: 'builtin'|'custom'|'blank', tpl }
  const [selected, setSelected] = useState({ kind: "blank" });
  const [form, setForm] = useState({ name: "", type: "chat" });
  const [customTemplates, setCustomTemplates] = useState([]);
  const [saveTplAgent, setSaveTplAgent] = useState(null); // agent being saved as template
  const [busyId, setBusyId] = useState(null); // agent id with an in-flight clone

  const pickTemplate = (t) => {
    setSelected({ kind: "builtin", tpl: t });
    setForm({ name: t.name, type: t.type });
  };
  const pickCustom = (t) => {
    setSelected({ kind: "custom", tpl: t });
    setForm({ name: t.name, type: t.type || "chat" });
  };
  const pickBlank = () => {
    setSelected({ kind: "blank" });
    setForm({ name: "", type: "chat" });
  };
  const resetDialog = (v) => {
    setOpen(v);
    if (!v) { setSelected({ kind: "blank" }); setForm({ name: "", type: "chat" }); }
  };

  const load = () => api.get("/agents").then((r) => setAgents(r.data)).catch(() => setAgents([]));
  const loadTemplates = () => api.get("/templates").then((r) => setCustomTemplates(r.data)).catch(() => {});
  useEffect(() => { load(); loadTemplates(); }, []);

  const create = async () => {
    if (!form.name.trim()) { toast.error("Ge agenten ett namn"); return; }
    setCreating(true);
    try {
      let payload;
      if (selected.kind === "builtin") {
        payload = {
          name: form.name.trim(), type: form.type,
          welcome_message: selected.tpl.welcome_message,
          system_prompt: selected.tpl.system_prompt,
        };
      } else if (selected.kind === "custom") {
        payload = { ...(selected.tpl.config || {}), name: form.name.trim(), type: form.type };
      } else {
        payload = {
          name: form.name.trim(), type: form.type,
          welcome_message: form.type === "voice" ? "Hej och tack för att du ringer! Hur kan jag hjälpa dig idag?" : "Hej! Hur kan jag hjälpa dig idag?",
        };
      }
      const { data } = await api.post("/agents", payload);
      const label = selected.kind === "builtin" ? `mallen "${selected.tpl.label}"` : selected.kind === "custom" ? `mallen "${selected.tpl.name}"` : null;
      toast.success(label ? `Agent skapad från ${label}` : "Agenten skapades");
      resetDialog(false);
      navigate(`/app/agents/${data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message || "Could not create agent");
    } finally {
      setCreating(false);
    }
  };

  const clone = async (a) => {
    setBusyId(a.id);
    try {
      const { data } = await api.post(`/agents/${a.id}/clone`);
      toast.success(`"${a.name}" klonades`);
      load();
      navigate(`/app/agents/${data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Kunde inte klona agenten");
    } finally { setBusyId(null); }
  };

  const deleteTemplate = async (tid) => {
    try {
      await api.delete(`/templates/${tid}`);
      setCustomTemplates((ts) => ts.filter((t) => t.id !== tid));
      if (selected.kind === "custom" && selected.tpl.id === tid) pickBlank();
      toast.success("Mall borttagen");
    } catch { toast.error("Kunde inte ta bort mallen"); }
  };

  const remove = async (id) => {
    await api.delete(`/agents/${id}`);
    toast.success("Agenten togs bort");
    load();
  };

  return (
    <div className="p-6 sm:p-8">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display font-bold text-3xl tracking-tight">Agenter</h1>
          <p className="mt-1 text-zinc-500">Bygg, anpassa och lansera dina AI-agenter.</p>
        </div>
        <button data-testid="create-agent-button" onClick={() => setOpen(true)} disabled={!canEdit}
          title={!canEdit ? "Du har inte behörighet att skapa agenter" : undefined}
          className="inline-flex items-center gap-2 bg-[#0033FF] hover:bg-[#002BE6] text-white rounded-md px-5 py-2.5 font-semibold transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed">
          <Plus size={18} weight="bold" /> Ny agent
        </button>
      </div>

      {agents === null ? (
        <div className="mt-20 flex justify-center"><CircleNotch size={28} className="animate-spin text-[#0033FF]" /></div>
      ) : agents.length === 0 ? (
        <div data-testid="agents-empty-state" className="mt-10 border border-dashed border-zinc-300 rounded-xl bg-white p-16 text-center">
          <div className="mx-auto h-14 w-14 rounded-xl bg-[#0033FF]/10 flex items-center justify-center">
            <Robot size={28} weight="duotone" className="text-[#0033FF]" />
          </div>
          <h3 className="mt-5 font-display font-bold text-xl">Inga agenter än</h3>
          <p className="mt-2 text-zinc-500 max-w-sm mx-auto">Skapa din första AI-kundtjänstagent för chatt eller röst och träna den på din kunskap.</p>
          <button data-testid="empty-create-agent-button" onClick={() => setOpen(true)} disabled={!canEdit}
            className="mt-6 inline-flex items-center gap-2 bg-[#0033FF] hover:bg-[#002BE6] text-white rounded-md px-5 py-2.5 font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            <Plus size={18} weight="bold" /> Skapa agent
          </button>
        </div>
      ) : (
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {agents.map((a) => (
            <div key={a.id} data-testid={`agent-card-${a.id}`}
              className="group bg-white border border-zinc-200 rounded-lg p-5 hover:-translate-y-1 hover:shadow-md transition-all cursor-pointer"
              onClick={() => navigate(`/app/agents/${a.id}`)}>
              <div className="flex items-start justify-between">
                <div className="h-11 w-11 rounded-lg flex items-center justify-center"
                  style={{ background: `${a.primary_color || "#0033FF"}1A`, color: a.primary_color || "#0033FF" }}>
                  {a.type === "voice" ? <Phone size={22} weight="bold" /> : a.type === "autopilot" ? <Robot size={22} weight="bold" /> : <ChatCircleDots size={22} weight="bold" />}
                </div>
                <div onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button data-testid={`agent-menu-${a.id}`} className="p-1.5 rounded-md hover:bg-zinc-100 text-zinc-400">
                        <DotsThreeVertical size={20} weight="bold" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => navigate(`/app/agents/${a.id}`)}>
                        <PencilSimple size={16} className="mr-2" /> {canEdit ? "Redigera" : "Visa"}
                      </DropdownMenuItem>
                      {canEdit && (
                        <DropdownMenuItem data-testid={`agent-clone-${a.id}`} onClick={() => clone(a)} disabled={busyId === a.id}>
                          <Copy size={16} className="mr-2" /> Klona
                        </DropdownMenuItem>
                      )}
                      {canEdit && (
                        <DropdownMenuItem data-testid={`agent-save-template-${a.id}`} onClick={() => setSaveTplAgent(a)}>
                          <BookmarkSimple size={16} className="mr-2" /> Spara som mall
                        </DropdownMenuItem>
                      )}
                      {canEdit && (
                        <DropdownMenuItem onClick={() => remove(a.id)} className="text-red-600">
                          <Trash size={16} className="mr-2" /> Ta bort
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
              <h3 className="mt-4 font-display font-bold text-lg group-hover:text-[#0033FF] transition-colors">{a.name}</h3>
              <div className="mt-1 flex items-center gap-2 text-xs text-zinc-500 font-medium">
                <span className="uppercase tracking-wide">{a.type}</span>·<span className="font-mono">{modelLabel(a.model)}</span>
              </div>
              {isSuperAdmin && a.creator_name && (
                <span data-testid={`agent-creator-${a.id}`}
                  className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-[#0033FF]/5 text-[#0033FF] border border-[#0033FF]/15">
                  <User size={13} weight="bold" /> {a.creator_name}
                </span>
              )}
              <div className="mt-4 flex items-center justify-between">
                <StatusBadge status={a.status} />
                <div className="flex items-center gap-3 text-xs text-zinc-400">
                  <span className="flex items-center gap-1"><Books size={14} /> {a.kb_count ?? 0}</span>
                  <span className="flex items-center gap-1"><ChatsCircle size={14} /> {a.conversation_count ?? 0}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={resetDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Skapa en ny agent</DialogTitle>
            <DialogDescription>Börja från en färdig mall med ifylld grundprompt – eller bygg från en tom agent.</DialogDescription>
          </DialogHeader>
          <div className="space-y-5 py-2">
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Välj en mall</label>
              <div className="mt-2 grid sm:grid-cols-2 gap-2.5" data-testid="template-gallery">
                {AGENT_TEMPLATES.map((t) => {
                  const active = selected.kind === "builtin" && selected.tpl.id === t.id;
                  return (
                  <button key={t.id} data-testid={`template-${t.id}`} onClick={() => pickTemplate(t)}
                    className={`flex items-start gap-3 p-3 rounded-lg border text-left transition-all ${
                      active ? "border-[#0033FF] bg-[#0033FF]/5 ring-2 ring-[#0033FF]/15" : "border-zinc-200 hover:border-zinc-300"
                    }`}>
                    <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${active ? "bg-[#0033FF] text-white" : "bg-zinc-100 text-zinc-500"}`}>
                      <t.icon size={20} weight="bold" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-sm text-zinc-900">{t.label}</div>
                      <div className="text-xs text-zinc-500 leading-snug">{t.description}</div>
                    </div>
                  </button>
                  );
                })}
                <button data-testid="template-blank" onClick={pickBlank}
                  className={`flex items-center gap-3 p-3 rounded-lg border text-left transition-all ${
                    selected.kind === "blank" ? "border-[#0033FF] bg-[#0033FF]/5 ring-2 ring-[#0033FF]/15" : "border-zinc-200 hover:border-zinc-300"
                  }`}>
                  <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${selected.kind === "blank" ? "bg-[#0033FF] text-white" : "bg-zinc-100 text-zinc-500"}`}>
                    <Plus size={20} weight="bold" />
                  </div>
                  <div>
                    <div className="font-semibold text-sm text-zinc-900">Tom agent</div>
                    <div className="text-xs text-zinc-500 leading-snug">Börja från grunden utan förifylld prompt.</div>
                  </div>
                </button>
              </div>
            </div>

            {customTemplates.length > 0 && (
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500 flex items-center gap-1.5">
                  <Sparkle size={13} weight="fill" className="text-[#0033FF]" /> Dina mallar
                </label>
                <div className="mt-2 grid sm:grid-cols-2 gap-2.5" data-testid="custom-template-gallery">
                  {customTemplates.map((t) => {
                    const active = selected.kind === "custom" && selected.tpl.id === t.id;
                    const Icon = t.type === "voice" ? Phone : t.type === "autopilot" ? Robot : ChatCircleDots;
                    return (
                      <div key={t.id} data-testid={`custom-template-${t.id}`}
                        className={`group relative flex items-start gap-3 p-3 rounded-lg border text-left transition-all cursor-pointer ${
                          active ? "border-[#0033FF] bg-[#0033FF]/5 ring-2 ring-[#0033FF]/15" : "border-zinc-200 hover:border-zinc-300"
                        }`} onClick={() => pickCustom(t)}>
                        <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${active ? "bg-[#0033FF] text-white" : "bg-zinc-100 text-zinc-500"}`}>
                          <Icon size={20} weight="bold" />
                        </div>
                        <div className="min-w-0 pr-5">
                          <div className="font-semibold text-sm text-zinc-900 truncate">{t.name}</div>
                          <div className="text-xs text-zinc-500 leading-snug line-clamp-2">{t.description || "Sparad mall från en egen agent."}</div>
                        </div>
                        {canEdit && (
                          <button data-testid={`delete-custom-template-${t.id}`}
                            onClick={(e) => { e.stopPropagation(); deleteTemplate(t.id); }}
                            title="Ta bort mall"
                            className="absolute top-2 right-2 p-1 rounded text-zinc-300 hover:text-red-600 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity">
                            <X size={14} weight="bold" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div>
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Agentnamn</label>
              <input data-testid="new-agent-name-input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="mt-1.5 w-full bg-white border border-zinc-200 rounded-md px-4 py-2.5 focus:ring-2 focus:ring-[#0033FF]/20 focus:border-[#0033FF] outline-none"
                placeholder="t.ex. Supportassistent" autoFocus />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Kanal</label>
              <div className="mt-1.5 grid grid-cols-3 gap-3">
                {[
                  { v: "chat", icon: ChatCircleDots, label: "Chatt", desc: "Webbplats & widget" },
                  { v: "voice", icon: Phone, label: "Telefon", desc: "Pratar med kunder i realtid" },
                  { v: "autopilot", icon: Robot, label: "Autopilot", desc: "Löser ärenden automatiskt" },
                ].map((t) => (
                  <button key={t.v} data-testid={`new-agent-type-${t.v}`} onClick={() => setForm((f) => ({ ...f, type: t.v }))}
                    className={`flex flex-col items-start gap-1 p-4 rounded-lg border text-left transition-all ${
                      form.type === t.v ? "border-[#0033FF] bg-[#0033FF]/5 ring-2 ring-[#0033FF]/15" : "border-zinc-200 hover:border-zinc-300"
                    }`}>
                    <t.icon size={22} weight="bold" className={form.type === t.v ? "text-[#0033FF]" : "text-zinc-500"} />
                    <span className="font-semibold mt-1">{t.label}</span>
                    <span className="text-xs text-zinc-500">{t.desc}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <button data-testid="confirm-create-agent-button" onClick={create} disabled={creating}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#0033FF] hover:bg-[#002BE6] text-white rounded-md px-5 py-2.5 font-semibold transition-colors disabled:opacity-60">
              {creating && <CircleNotch size={18} className="animate-spin" />} Skapa agent
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SaveTemplateDialog agent={saveTplAgent} onClose={() => setSaveTplAgent(null)} onSaved={loadTemplates} />
    </div>
  );
}

function SaveTemplateDialog({ agent, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (agent) { setName(`${agent.name} (mall)`); setDescription(""); }
  }, [agent]);

  const save = async () => {
    if (!name.trim()) { toast.error("Ge mallen ett namn"); return; }
    setSaving(true);
    try {
      await api.post(`/agents/${agent.id}/save-as-template`, { name: name.trim(), description: description.trim() });
      toast.success(`Mallen "${name.trim()}" sparades`);
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Kunde inte spara mallen");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!agent} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-md" data-testid="save-template-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Spara som mall</DialogTitle>
          <DialogDescription>
            Spara den här agentens inställningar (prompt, personlighet, beteende, varumärke, workflows) som en återanvändbar mall. Kunskapsbas och hemliga nycklar ingår inte.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Mallnamn</label>
            <input data-testid="template-name-input" value={name} onChange={(e) => setName(e.target.value)}
              className="mt-1.5 w-full bg-white border border-zinc-200 rounded-md px-4 py-2.5 focus:ring-2 focus:ring-[#0033FF]/20 focus:border-[#0033FF] outline-none"
              placeholder="t.ex. E-handel returbot" autoFocus />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Beskrivning (valfri)</label>
            <textarea data-testid="template-description-input" value={description} onChange={(e) => setDescription(e.target.value)}
              rows={2} maxLength={200}
              className="mt-1.5 w-full bg-white border border-zinc-200 rounded-md px-4 py-2.5 focus:ring-2 focus:ring-[#0033FF]/20 focus:border-[#0033FF] outline-none resize-none"
              placeholder="Kort beskrivning av vad mallen är till för." />
          </div>
        </div>
        <DialogFooter>
          <button data-testid="confirm-save-template-button" onClick={save} disabled={saving}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#0033FF] hover:bg-[#002BE6] text-white rounded-md px-5 py-2.5 font-semibold transition-colors disabled:opacity-60">
            {saving && <CircleNotch size={18} className="animate-spin" />} Spara mall
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
